import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { IreneLogo } from "@/components/irene-logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Check, Loader2, ArrowRight, Sparkles } from "lucide-react"
import { useAuth } from "@/lib/auth-context"
import { createCheckoutSession } from "@/lib/api-client"
import { useToast } from "@/components/ui/use-toast"
import { PRODUCTS } from "@/lib/products"

export default function StripeTrialPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState<string | null>(null)

  const handleSelectPlan = async (priceId?: string) => {
    if (!priceId) {
      // Free plan — go straight to dashboard
      navigate("/dashboard")
      return
    }

    setIsLoading(priceId)

    try {
      const { url } = await createCheckoutSession(priceId)
      window.location.href = url
    } catch (error: any) {
      console.error("Error creating checkout session:", error)
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      })
      setIsLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-6xl py-12">
        {/* Header */}
        <div className="mb-12 flex flex-col items-center text-center">
          <IreneLogo size={56} className="mb-4" />
          <h1 className="text-4xl font-bold text-foreground">
            Choose Your Plan
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">
            Start free, upgrade when you're ready. All paid plans include a 14-day trial.
          </p>
          {user && (
            <p className="mt-1 text-sm text-muted-foreground">
              Welcome, {user.name}!
            </p>
          )}
        </div>

        {/* Plans */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((plan) => (
            <Card
              key={plan.name}
              className={
                plan.highlighted
                  ? "relative border-primary shadow-lg"
                  : "border-border"
              }
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    <Sparkles className="h-3 w-3" />
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-foreground">
                    {plan.price === 0 ? "Free" : `$${plan.price}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-muted-foreground">/{plan.interval}</span>
                  )}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {plan.description}
                </p>
              </CardHeader>
              <CardContent>
                <ul className="mb-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSelectPlan(plan.priceId)}
                  disabled={isLoading !== null}
                  className="w-full"
                  variant={plan.highlighted ? "default" : "outline"}
                >
                  {isLoading === plan.priceId ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : plan.price === 0 ? (
                    <>
                      Get Started Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features */}
        <div className="mt-16 rounded-lg border border-border bg-card p-6">
          <h2 className="mb-4 text-center text-xl font-semibold text-foreground">
            All paid plans include:
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="font-medium text-foreground">14-day free trial</p>
                <p className="text-sm text-muted-foreground">
                  No credit card required
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="font-medium text-foreground">Cancel anytime</p>
                <p className="text-sm text-muted-foreground">
                  No long-term contracts
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="font-medium text-foreground">Secure & private</p>
                <p className="text-sm text-muted-foreground">
                  Your data is encrypted at rest
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
